import React from "react";
import {
  Pagination as PaginationUI,
  NewsCard,
  NewsList,
  NewsShortList,
} from "@caelor/ui";
import { useTranslation } from "react-i18next";
import { useNewsStore } from "../../../stores/use-news-store";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { useStyleguideColors } from "../../../hooks/use-styleguide-colors";
import { DEFAULT_LABELS_COLOR, DEFAULT_SPACES_COLOR } from "../defaults";
import { htmlDecode } from "../useBlogs";
import { BlogElements } from "../../../optics/components/drawers/settings/features/news/defaults";
import {
  NewPaginationTypesMapper,
  PaginationTypes,
} from "@Shared/Pagination-Types";
import {
  PaginationWrapper,
  BlogCardContainer,
  BlogListContainer,
  Link,
} from "./styled";
import styled from "styled-components";

const LinkWrapper = ({ url, mode, openInNewTab, children }) => {
  if (mode === "view") {
    return (
      <Link
        href={url}
        target={openInNewTab ? "_blank" : "_top"}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  }
  return children;
};

const MagazineGridContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ reverse }) => (reverse ? "2fr 3fr" : "3fr 2fr")};
  grid-gap: 16px;
  align-items: stretch;

  .card {
    height: 100%;

    & > div {
      min-height: 100%;
      box-sizing: border-box;
    }
  }

  .short-list {
    display: grid;
    row-gap: 12px;
  }
`;

const Variant = ({ variant, data, columns, children }) => {
  if (variant === "magazine") {
    const groups = [];

    for (let i = 0; i < data.length; i += 4) {
      const [card, ...shorts] = data.slice(i, i + 4);
      groups.push({ card, shorts: shorts.slice(0, 3) });
    }

    return (
      <>
        {groups.map((group, groupIndex) => {
          const isEven = groupIndex % 2 === 0;
          const reverse = !isEven;

          return (
            <MagazineGridContainer key={groupIndex} reverse={reverse}>
              {isEven ? (
                <>
                  <div className="card">
                    {group.card &&
                      children(NewsCard, group.card, groupIndex * 4)}
                  </div>
                  <div className="short-list">
                    {group.shorts.map((blog, index) => {
                      const { createdBy, ...blogData } = blog;
                      return children(
                        NewsShortList,
                        blogData,
                        groupIndex * 4 + index + 1
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className="short-list">
                    {group.shorts.map((blog, index) => {
                      const { createdBy, ...blogData } = blog;
                      return children(
                        NewsShortList,
                        blogData,
                        groupIndex * 4 + index + 1
                      );
                    })}
                  </div>
                  <div className="card">
                    {group.card &&
                      children(NewsCard, group.card, groupIndex * 4)}
                  </div>
                </>
              )}
            </MagazineGridContainer>
          );
        })}
      </>
    );
  }

  const Component =
    variant === "card"
      ? NewsCard
      : variant === "list"
      ? NewsList
      : NewsShortList;

  const Container = variant === "list" ? BlogListContainer : BlogCardContainer;

  return (
    <Container columns={columns}>
      {data.map((blog, index) => children(Component, blog, index))}
    </Container>
  );
};

const Pagination = ({ data, pagination, queryPagination, children }) => {
  const { t } = useTranslation();

  const buildData = () => {
    if (pagination === PaginationTypes.INFINITE || queryPagination.disabled) {
      return Object.keys(data || {})?.flatMap((key) => data[key]) || [];
    }
    if (pagination === PaginationTypes.CAROUSEL) {
      return data[queryPagination.page] || [];
    }
    return [];
  };

  if (queryPagination.disabled) return children(buildData());

  const { hasNextPage, hasPrevPage, onNext, onPrev } = queryPagination;

  return (
    <PaginationWrapper>
      {children(buildData())}
      <PaginationUI
        type={NewPaginationTypesMapper[pagination]}
        labels={{
          next:
            pagination === PaginationTypes.INFINITE
              ? t("show-more")
              : t("next"),
          prev: t("prev"),
        }}
        hasNext={hasNextPage}
        hasPrev={hasPrevPage}
        onNext={onNext}
        onPrev={onPrev}
      />
    </PaginationWrapper>
  );
};

function NewsPreview({ data, queryPagination, mode = "edit" }) {
  const { config } = useNewsStore((state) => ({
    config: state.data.config,
  }));

  const { styleguideSetup } = useOpticsStore((state) => ({
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  const {
    variant,
    columns,
    pagination,
    activeElements,
    cardBorderRadius,
    colors,
    openInNewTab,
  } = config;

  const shouldShowElement = (element) => activeElements?.includes(element);

  return (
    <Pagination
      data={data}
      pagination={pagination}
      queryPagination={queryPagination}
    >
      {(paginatedData) => (
        <Variant variant={variant} data={paginatedData} columns={columns}>
          {(BlogComponent, blog, index) => {
            if (!blog) return null;
            return (
              <LinkWrapper
                url={blog.url}
                mode={mode}
                openInNewTab={openInNewTab ?? true}
              >
                <BlogComponent
                  {...blog}
                  key={blog.contentId || index}
                  blogId={blog.contentId}
                  title={blog.name}
                  coverPictureCallback={blog.coverPictureCallback}
                  description={
                    shouldShowElement(BlogElements.DESCRIPTION)
                      ? htmlDecode(blog.excerpt)
                      : null
                  }
                  authorName={
                    shouldShowElement(BlogElements.AUTHOR)
                      ? blog.createdBy
                      : null
                  }
                  authorUrl={blog.avatarUrl}
                  createdDate={
                    shouldShowElement(BlogElements.DATE) ? blog.created : null
                  }
                  space={
                    shouldShowElement(BlogElements.SPACE)
                      ? blog.space?.name
                      : null
                  }
                  labels={
                    shouldShowElement(BlogElements.LABEL) ? blog.labels : []
                  }
                  borderRadius={cardBorderRadius}
                  spaceColor={
                    colors || styleguideColors || DEFAULT_SPACES_COLOR
                  }
                  labelsColor={
                    colors || styleguideColors || DEFAULT_LABELS_COLOR
                  }
                  columns={columns}
                  isMagazine={variant === "magazine"}
                  shouldShowImage={shouldShowElement(BlogElements.IMAGE)}
                />
              </LinkWrapper>
            );
          }}
        </Variant>
      )}
    </Pagination>
  );
}

export default NewsPreview;
